import { roundTo2, roundTo2Str } from "./numberUtils";
import { getPaymentPlanDisplayName } from "./eftUtils";

/** Default HST on office expense line items (15%). */
export const DEFAULT_OFFICE_EXPENSE_TAX_RATE = 0.15;

export const OFFICE_EXPENSE_OPTIONS = [
  { key: "deskFee", label: "Desk fee" },
  { key: "courierFee", label: "Courier fee" },
  { key: "bankCharge", label: "Bank charge", taxExempt: true },
  { key: "otherFee", label: "Other fee" },
  { key: "commissionDeduction", label: "Commission deduction" },
  { key: "garnishment", label: "Garnishment", taxExempt: true },
];

export function isOfficeExpenseTaxExempt(key) {
  return OFFICE_EXPENSE_OPTIONS.some(
    (opt) => opt.key === key && opt.taxExempt === true,
  );
}

export function defaultOfficeExpenseItems() {
  return OFFICE_EXPENSE_OPTIONS.map((opt) => ({
    key: opt.key,
    label: opt.label,
    enabled: false,
    amount: "",
  }));
}

/** Parse tax rate stored as decimal (0.15) or whole percent (15). */
export function parseOfficeExpenseTaxRate(value) {
  if (value === undefined || value === null || value === "") {
    return DEFAULT_OFFICE_EXPENSE_TAX_RATE;
  }
  const n = parseFloat(String(value).replace(/,/g, ""));
  if (Number.isNaN(n)) return DEFAULT_OFFICE_EXPENSE_TAX_RATE;
  if (n > 1 && n <= 100) return n / 100;
  if (n >= 0 && n <= 1) return n;
  return DEFAULT_OFFICE_EXPENSE_TAX_RATE;
}

export function normalizeOfficeExpenseItems(items) {
  const defaults = defaultOfficeExpenseItems();
  if (!Array.isArray(items) || items.length === 0) return defaults;
  return defaults.map((def) => {
    const found = items.find((i) => i?.key === def.key);
    if (!found) return def;
    return {
      ...def,
      ...found,
      key: def.key,
      label: def.label,
      enabled: !!(found.enabled === true || found.enabled === "true"),
      amount: found.amount ?? "",
    };
  });
}

/** Effective office-expense tax rate for an agent (single rate for all lines). */
export function getAgentOfficeExpensesTaxRate(agent) {
  if (agent?.officeExpensesTaxRate != null && agent.officeExpensesTaxRate !== "") {
    return parseOfficeExpenseTaxRate(agent.officeExpensesTaxRate);
  }
  const items = agent?.officeExpenseItems;
  if (Array.isArray(items) && items.length > 0) {
    const withRate = items.find((i) => i?.taxRate != null && i.taxRate !== "");
    if (withRate) return parseOfficeExpenseTaxRate(withRate.taxRate);
  }
  return DEFAULT_OFFICE_EXPENSE_TAX_RATE;
}

/**
 * Sum enabled office expense lines (amount + tax at shared rate).
 * @returns {{ subTotal: number, taxTotal: number, total: number, lines: Array }}
 */
export function computeOfficeExpenses(items, hasOfficeExpenses, taxRateInput) {
  const empty = { subTotal: 0, taxTotal: 0, total: 0, lines: [] };
  if (!hasOfficeExpenses) return empty;

  const globalRate = parseOfficeExpenseTaxRate(taxRateInput);
  const normalized = normalizeOfficeExpenseItems(items);
  let subTotal = 0;
  let taxTotal = 0;
  const lines = [];

  for (const item of normalized) {
    if (!item.enabled) continue;
    const amount = roundTo2(parseFloat(String(item.amount || "").replace(/,/g, "")) || 0);
    if (amount <= 0) continue;
    const taxExempt = isOfficeExpenseTaxExempt(item.key);
    const taxRate = taxExempt ? 0 : globalRate;
    const tax = roundTo2(amount * taxRate);
    const lineTotal = roundTo2(amount + tax);
    subTotal = roundTo2(subTotal + amount);
    taxTotal = roundTo2(taxTotal + tax);
    lines.push({
      key: item.key,
      label: item.label,
      amount: roundTo2Str(amount),
      taxRate,
      tax: roundTo2Str(tax),
      total: roundTo2Str(lineTotal),
      taxExempt,
    });
  }

  return {
    subTotal,
    taxTotal,
    total: roundTo2(subTotal + taxTotal),
    lines,
  };
}

export function getAgentOfficeExpenseAmounts(agent) {
  return computeOfficeExpenses(
    agent?.officeExpenseItems,
    agent?.hasOfficeExpenses === true || agent?.hasOfficeExpenses === "true",
    getAgentOfficeExpensesTaxRate(agent),
  );
}

/**
 * Split office expenses so commission deduction can show as its own EFT/YTD line.
 * @returns {{
 *   commissionDeduction: { subTotal: number, taxTotal: number, total: number },
 *   other: { subTotal: number, taxTotal: number, total: number },
 *   all: ReturnType<typeof getAgentOfficeExpenseAmounts>
 * }}
 */
export function splitAgentOfficeExpenseAmounts(agent) {
  const all = getAgentOfficeExpenseAmounts(agent);
  const empty = { subTotal: 0, taxTotal: 0, total: 0 };
  const commissionDeduction = { ...empty };
  const other = { ...empty };
  for (const line of all.lines) {
    const amount = parseFloat(line.amount) || 0;
    const tax = parseFloat(line.tax) || 0;
    const lineTotal = parseFloat(line.total) || 0;
    const bucket =
      line.key === "commissionDeduction" ? commissionDeduction : other;
    bucket.subTotal = roundTo2(bucket.subTotal + amount);
    bucket.taxTotal = roundTo2(bucket.taxTotal + tax);
    bucket.total = roundTo2(bucket.total + lineTotal);
  }
  return { commissionDeduction, other, all };
}

/** Sum split office expenses across agents (for EFT / YTD display). */
export function sumSplitAgentOfficeExpenses(agents) {
  const commissionDeduction = { subTotal: 0, taxTotal: 0, total: 0 };
  const other = { subTotal: 0, taxTotal: 0, total: 0 };
  for (const agent of agents || []) {
    const split = splitAgentOfficeExpenseAmounts(agent);
    commissionDeduction.subTotal = roundTo2(
      commissionDeduction.subTotal + split.commissionDeduction.subTotal,
    );
    commissionDeduction.taxTotal = roundTo2(
      commissionDeduction.taxTotal + split.commissionDeduction.taxTotal,
    );
    commissionDeduction.total = roundTo2(
      commissionDeduction.total + split.commissionDeduction.total,
    );
    other.subTotal = roundTo2(other.subTotal + split.other.subTotal);
    other.taxTotal = roundTo2(other.taxTotal + split.other.taxTotal);
    other.total = roundTo2(other.total + split.other.total);
  }
  return { commissionDeduction, other };
}

/** Which column (listing vs selling) an agent's office expense belongs in. */
function expenseColumnForAgent(agent, isCoOperatingSide) {
  if (parseFloat(agent.agentSelling || 0) > 0) return "selling";
  if (parseFloat(agent.agentListing || 0) > 0) return "listing";
  return isCoOperatingSide ? "selling" : "listing";
}

/** Per-agent office expense rows for Financial sheet (nested under agent). */
export function buildAgentOfficeExpenseSheetLines(agent, isCoOperatingSide) {
  const exp = getAgentOfficeExpenseAmounts(agent);
  const onSelling = expenseColumnForAgent(agent, isCoOperatingSide) === "selling";
  return exp.lines.map((line) => {
    const amount = parseFloat(line.amount) || 0;
    const tax = parseFloat(line.tax) || 0;
    const listing = onSelling ? 0 : amount;
    const selling = onSelling ? amount : 0;
    return {
      key: line.key,
      label: line.label,
      listing: roundTo2Str(listing),
      selling: roundTo2Str(selling),
      subTotal: line.amount,
      hst: line.tax,
      total: line.total,
    };
  });
}

function sumAgentOfficeExpenses(agents) {
  let subTotal = 0;
  let taxTotal = 0;
  let total = 0;
  for (const agent of agents || []) {
    const exp = getAgentOfficeExpenseAmounts(agent);
    subTotal = roundTo2(subTotal + exp.subTotal);
    taxTotal = roundTo2(taxTotal + exp.taxTotal);
    total = roundTo2(total + exp.total);
  }
  return { subTotal, taxTotal, total };
}

/** Office expense totals split by Financial sheet listing/selling columns. */
export function sumAgentOfficeExpensesByColumn(agents, isCoOperatingSide) {
  let listing = 0;
  let selling = 0;
  let subTotal = 0;
  let taxTotal = 0;
  let total = 0;
  for (const agent of agents || []) {
    for (const line of buildAgentOfficeExpenseSheetLines(agent, isCoOperatingSide)) {
      listing = roundTo2(listing + (parseFloat(line.listing) || 0));
      selling = roundTo2(selling + (parseFloat(line.selling) || 0));
      subTotal = roundTo2(subTotal + (parseFloat(line.subTotal) || 0));
      taxTotal = roundTo2(taxTotal + (parseFloat(line.hst) || 0));
      total = roundTo2(total + (parseFloat(line.total) || 0));
    }
  }
  return { listing, selling, subTotal, taxTotal, total };
}

export { sumAgentOfficeExpenses };

function isReferralAgentRow(a) {
  return (a?.classification || "").toUpperCase() === "REFERRAL";
}

/**
 * Office expense lines for Financial / Trade Details sheet (listing vs selling columns).
 */
export function buildTradeOfficeExpenseSheetLines(trade, options = {}) {
  const isCoOperatingSide =
    options.isCoOperatingSide ??
    (trade?.keyInfo?.classification || "") === "CO-OPERATING SIDE";
  const agents = (options.agents || trade?.agentCommissionList || []).filter(
    (a) => !isReferralAgentRow(a),
  );

  const lines = [];
  let totalListing = 0;
  let totalSelling = 0;
  let totalSub = 0;
  let totalTax = 0;
  let totalTotal = 0;

  for (const agent of agents) {
    const exp = getAgentOfficeExpenseAmounts(agent);
    const onSelling =
      expenseColumnForAgent(agent, isCoOperatingSide) === "selling";
    for (const line of exp.lines) {
      const amount = parseFloat(line.amount) || 0;
      const tax = parseFloat(line.tax) || 0;
      const lineTotal = parseFloat(line.total) || 0;
      const listing = onSelling ? 0 : amount;
      const selling = onSelling ? amount : 0;

      lines.push({
        key: `${agent.agentId || agent.agentName}-${line.key}`,
        label: line.label,
        agentName: agent.agentName || "",
        listing: roundTo2Str(listing),
        selling: roundTo2Str(selling),
        subTotal: line.amount,
        hst: line.tax,
        total: line.total,
        taxRate: line.taxRate,
      });

      totalListing = roundTo2(totalListing + listing);
      totalSelling = roundTo2(totalSelling + selling);
      totalSub = roundTo2(totalSub + amount);
      totalTax = roundTo2(totalTax + tax);
      totalTotal = roundTo2(totalTotal + lineTotal);
    }
  }

  return {
    lines,
    totals: {
      listing: roundTo2Str(totalListing),
      selling: roundTo2Str(totalSelling),
      subTotal: roundTo2Str(totalSub),
      hst: roundTo2Str(totalTax),
      total: roundTo2Str(totalTotal),
    },
  };
}

/** Fee plan + office expense rows for Fees Deduction tables. */
export function buildTradeFeesDeductionRows(trade) {
  const rows = [];
  const agents = trade?.agentCommissionList || [];
  const isDoubleEnder =
    (trade?.keyInfo?.classification || "") === "AGENT DOUBLE ENDER";

  const list = isDoubleEnder
    ? agents.filter(
        (a) =>
          (a.classification || "").toUpperCase() !== "AGENT DOUBLE ENDER" ||
          (a.transactionType || "").toUpperCase() === "SELLING",
      )
    : agents;

  for (const agent of list) {
    if (parseFloat(agent.feesDeducted || 0) > 0 || agent.feeInfo) {
      rows.push({
        agentName: agent.agentName || "",
        feeType: getPaymentPlanDisplayName(agent.feeInfo) || agent.feeInfo || "Fee plan",
        deductedFee: agent.feesDeducted || "0.00",
        taxed: "Yes",
      });
    }
    const officeExp = getAgentOfficeExpenseAmounts(agent);
    for (const line of officeExp.lines) {
      rows.push({
        agentName: agent.agentName || "",
        feeType: line.label,
        deductedFee: line.amount,
        taxed: line.taxExempt
          ? "No"
          : `Yes (${Math.round(line.taxRate * 100)}%)`,
        lineTotal: line.total,
      });
    }
    if (
      String(agent.buyerRebateIncluded || "").toLowerCase() === "yes" &&
      parseFloat(agent.buyerRebateAmount || 0) > 0
    ) {
      rows.push({
        agentName: agent.agentName || "",
        feeType: "Buyer Rebate",
        deductedFee: agent.buyerRebateAmount,
        taxed: "No",
      });
    }
  }
  return rows;
}
